import React, { useEffect, useState } from "react";
import {
  Box,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import SearchBar from "material-ui-search-bar";
import SearchIcon from "@material-ui/icons/Search";
import moment from "moment";
import { useSnackbar } from "notistack";

import TableComponent from "../../components/table";
import CardComponent from "../../components/Card";
import ButtonWithLoading from "../../components/ButtonWithLoading";
import DialogComponent from "../../components/Dialog";
import { Get } from "../../actions";
import {
  Get_AllLogsWithPagination_URL,
  Get_LogFilterOptions_URL,
} from "../../constants/apiUrls";
import { getTranslation } from "../../heplers/translationHelper";
import { format } from "../../heplers/format";

const useStyles = makeStyles((theme) => ({
  searchBar: {
    borderRadius: "20px",
    marginBottom: "20px",
  },
  detail: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontFamily: "monospace",
    fontSize: "13px",
    margin: 0,
  },
}));

const levelColor = (levelName) => {
  switch (levelName) {
    case "Error":
      return { backgroundColor: "#fdecea", color: "#b71c1c" };
    case "Warning":
      return { backgroundColor: "#fff4e5", color: "#8a5300" };
    default:
      return { backgroundColor: "#e8f4fd", color: "#0d3c61" };
  }
};

const columns = [
  {
    id: "createdAt",
    numeric: false,
    disablePadding: true,
    label: getTranslation("Date/Time", "Date/Time", "Date/Time"),
    component: ({ row }) => (
      <Typography noWrap>{format("dateTime", row?.createdAt)}</Typography>
    ),
  },
  {
    id: "levelName",
    numeric: false,
    disablePadding: true,
    label: getTranslation("Level", "Level", "Level"),
    component: ({ row }) => (
      <Chip size="small" label={row?.levelName} style={levelColor(row?.levelName)} />
    ),
  },
  {
    id: "typeName",
    numeric: false,
    disablePadding: true,
    label: getTranslation("Type", "Type", "Type"),
    component: ({ row }) => <Typography noWrap>{row?.typeName}</Typography>,
  },
  {
    id: "source",
    numeric: false,
    disablePadding: true,
    label: getTranslation("Source", "Source", "Source"),
    component: ({ row }) => <Typography noWrap>{row?.source || "-"}</Typography>,
  },
  {
    id: "message",
    numeric: false,
    disablePadding: false,
    label: getTranslation("Message", "Message", "Message"),
    component: ({ row }) => (
      <Tooltip title={row?.message || ""}>
        <Typography>{row?.message}</Typography>
      </Tooltip>
    ),
  },
  {
    id: "customerId",
    numeric: false,
    disablePadding: true,
    label: getTranslation("Customer", "Customer", "Customer"),
    component: ({ row }) => <Typography noWrap>{row?.customerId || "-"}</Typography>,
  },
  {
    id: "durationMs",
    numeric: true,
    disablePadding: true,
    label: getTranslation("Duration", "Duration", "Duration"),
    component: ({ row }) => (
      <Typography noWrap>
        {row?.durationMs || row?.durationMs === 0 ? `${row.durationMs} ms` : "-"}
      </Typography>
    ),
  },
  {
    id: "actions",
    numeric: true,
    disablePadding: false,
    align: "right",
    label: getTranslation("Details", "Details", "Details"),
  },
];

const Logs = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [logs, setLogs] = useState([]);
  const [types, setTypes] = useState([]);
  const [levels, setLevels] = useState([]);
  const [type, setType] = useState("");
  const [level, setLevel] = useState("");
  const [searchString, setSearchString] = useState("");
  const [fromDate, setFromDate] = useState(
    moment().subtract(7, "days").format("YYYY-MM-DD")
  );
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);

  const [page, setPage] = useState(0);
  let pageSize = localStorage.getItem("sizeAppLogs");
  if (pageSize == null) {
    localStorage.setItem("sizeAppLogs", 10);
    pageSize = 10;
  }
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);

  useEffect(() => {
    Get(
      {},
      Get_LogFilterOptions_URL,
      null,
      (resp) => {
        setTypes(resp?.data?.types ?? []);
        setLevels(resp?.data?.levels ?? []);
      },
      () => {}
    );
  }, []);

  useEffect(() => {
    getLogs();
    // eslint-disable-next-line
  }, [page, rowsPerPage]);

  const getLogs = () => {
    setIsLoading(true);
    const params = {
      searchString: searchString,
      pageNumber: page,
      pageSize: rowsPerPage,
      sort: "createdAt",
      sortDirection: 1,
      fromDate: fromDate,
      toDate: toDate,
    };
    // Leave the filter out entirely when "All" is selected.
    if (type !== "") params.type = type;
    if (level !== "") params.level = level;

    Get(
      params,
      Get_AllLogsWithPagination_URL,
      null,
      (resp) => {
        setLogs(resp.data);
        setIsLoading(false);
      },
      (error) => {
        enqueueSnackbar(error?.data ? error?.data : "internal server error", {
          variant: "error",
        });
        setIsLoading(false);
      }
    );
  };

  const applyFilter = () => {
    if (page !== 0) {
      setPage(0); // triggers the effect, which refetches
    } else {
      getLogs();
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    localStorage.setItem("sizeAppLogs", parseInt(event.target.value));
    setRowsPerPage(localStorage.getItem("sizeAppLogs"));
    setPage(0);
  };

  return (
    <div>
      <CardComponent title={getTranslation("Logs", "Journaux", "Protokolle")}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <SearchBar
                value={searchString}
                onCancelSearch={() => setSearchString("")}
                onChange={(value) => setSearchString(value)}
                onRequestSearch={() => applyFilter()}
                searchIcon={
                  <SearchIcon style={{ fontSize: "32px", marginRight: "20px" }} />
                }
                className={classes.searchBar}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label={getTranslation("Type", "Type", "Type")}
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <MenuItem value="">
                  {getTranslation("All", "All", "All")}
                </MenuItem>
                {types.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label={getTranslation("Level", "Level", "Level")}
                value={level}
                onChange={(e) => setLevel(e.target.value)}
              >
                <MenuItem value="">
                  {getTranslation("All", "All", "All")}
                </MenuItem>
                {levels.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                id="fromDate"
                type="date"
                size="small"
                variant="outlined"
                label={getTranslation("From", "From", "From")}
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                id="toDate"
                type="date"
                size="small"
                variant="outlined"
                label={getTranslation("To", "To", "To")}
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={1}>
              <ButtonWithLoading
                label={getTranslation("Filter", "Filter", "Filter")}
                size="medium"
                variant="contained"
                color="primary"
                onClick={() => applyFilter()}
              />
            </Grid>
          </Grid>

          <TableComponent
            dense
            loading={isLoading}
            colums={columns}
            count={logs?.totalCount}
            rows={logs?.data}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={(event, newPage) => handleChangePage(event, newPage)}
            handleChangeRowsPerPage={(e) => handleChangeRowsPerPage(e)}
            actions={[
              {
                component: (rowData) => (
                  <ButtonWithLoading
                    label={getTranslation("View", "View", "View")}
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => setSelectedLog(rowData)}
                  />
                ),
              },
            ]}
          />
        </CardContent>
      </CardComponent>

      <DialogComponent
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        title={getTranslation("Log Details", "Log Details", "Log Details")}
        fullWidth
        maxWidth="md"
      >
        {selectedLog ? (
          <Box p={2}>
            <Typography variant="subtitle2">
              {format("dateTime", selectedLog.createdAt)} &nbsp;|&nbsp;{" "}
              {selectedLog.levelName} &nbsp;|&nbsp; {selectedLog.typeName}
            </Typography>
            <Box mt={1}>
              <Typography variant="body2">
                <b>{getTranslation("Source", "Source", "Source")}:</b>{" "}
                {selectedLog.source || "-"}
              </Typography>
              <Typography variant="body2">
                <b>{getTranslation("Reference", "Reference", "Reference")}:</b>{" "}
                {selectedLog.reference || "-"}
              </Typography>
              <Typography variant="body2">
                <b>{getTranslation("Customer", "Customer", "Customer")}:</b>{" "}
                {selectedLog.customerId || "-"}
              </Typography>
              <Typography variant="body2">
                <b>{getTranslation("Duration", "Duration", "Duration")}:</b>{" "}
                {selectedLog.durationMs || selectedLog.durationMs === 0
                  ? `${selectedLog.durationMs} ms`
                  : "-"}
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant="body2">
                <b>{getTranslation("Message", "Message", "Message")}</b>
              </Typography>
              <Typography variant="body2">{selectedLog.message}</Typography>
            </Box>
            {selectedLog.detail ? (
              <Box mt={2}>
                <Typography variant="body2">
                  <b>{getTranslation("Detail", "Detail", "Detail")}</b>
                </Typography>
                <pre className={classes.detail}>{selectedLog.detail}</pre>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </DialogComponent>
    </div>
  );
};

export default Logs;
